import { useQuery } from '@tanstack/react-query';
import { fetchAnalyticsSummary, AnalyticsSummaryParams } from '../services/analyticsService';
import { AnalyticsSummaryResponse } from '../types/analytics';
import { useTranslation } from 'react-i18next';

/**
 * Custom hook to fetch and manage analytics summary data
 * @param params - Parameters for the analytics summary request
 * @returns Query result with analytics summary data
 */
export const useAnalyticsSummary = (params: AnalyticsSummaryParams) => {
  const { i18n } = useTranslation();
  
  // If no language is specified, use the current app language
  const queryParams = {
    ...params,
    applanguage: params.applanguage || i18n.language,
  };

  return useQuery<AnalyticsSummaryResponse, Error>({
    queryKey: ['analyticsSummary', queryParams],
    queryFn: () => fetchAnalyticsSummary(queryParams),
    enabled: params.enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    retry: 2,
    select: (data) => {
      // You can transform the data here if needed
      return data;
    }
  });
};

/**
 * Helper function to get default date range for analytics
 * @param days - Number of days to include in the range (default: 30)
 * @returns Object with start and end dates formatted as YYYY-MM-DD
 */
export const getDefaultDateRange = (days = 30) => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);
  
  return {
    start: formatDate(start),
    end: formatDate(end)
  };
};

/**
 * Format a date as YYYY-MM-DD
 * @param date - Date to format
 * @returns Formatted date string
 */
const formatDate = (date: Date): string => {
  return date.toISOString().split('T')[0];
}; 